import { supabase } from '@/integrations/supabase/client';
import type { CommunityTab, CommunityTag } from '@/types/community';
import { listAreas, rpc } from './cerebro';

export async function fetchCommunityTabs(): Promise<CommunityTab[]> {
  const [areas, channels] = await Promise.all([
    listAreas(),
    rpc<Array<{ id: string; slug: string; name: string; description: string | null; position: number; is_locked: boolean }>>('list_chat_channels').catch(() => []),
  ]);
  const forumTabs = areas.map(area => ({
    id: area.id, name: area.name, slug: area.slug, label: area.name,
    tab_type: 'forum', position: area.position, is_enabled: true,
    icon: area.icon, allow_replies: true, grid_columns: 1,
    auto_delete_enabled: false, auto_delete_days: 0, min_level_id: null,
    is_locked: area.is_locked, is_visitor_locked: true,
    is_post_locked: !area.can_post, is_visitor_post_locked: true,
    min_points_required: 0, is_quick_access: true,
    mobile_position: area.position, bonus_points_per_post: 0,
  }));
  const chatTabs: CommunityTab[] = channels.map((channel, index) => ({
    id: channel.id, name: channel.name, slug: channel.slug, label: channel.name,
    tab_type: 'chat', position: 100 + index, is_enabled: true,
    icon: 'messages-square', allow_replies: true, grid_columns: 1,
    auto_delete_enabled: false, auto_delete_days: 0, min_level_id: null,
    is_locked: channel.is_locked, is_visitor_locked: true,
    is_post_locked: channel.is_locked, is_visitor_post_locked: true,
    min_points_required: 0, is_quick_access: true,
    mobile_position: 100 + index, bonus_points_per_post: 0,
  }));
  const special = [
    ['00000000-0000-4000-8000-000000000201', 'ensino', 'Ensino', 'ensino', 'book-open', 200],
    ['00000000-0000-4000-8000-000000000202', 'desafios', 'Desafios', 'desafio', 'trophy', 210],
    ['00000000-0000-4000-8000-000000000203', 'eventos', 'Eventos', 'evento', 'calendar', 220],
  ].map(([id, slug, name, tab_type, icon, position]) => ({
    id: String(id), name: String(name), slug: String(slug), label: String(name),
    tab_type: String(tab_type), position: Number(position), is_enabled: true,
    icon: String(icon), allow_replies: true, grid_columns: 1,
    auto_delete_enabled: false, auto_delete_days: 0, min_level_id: null,
    is_locked: false, is_visitor_locked: true, is_post_locked: false,
    is_visitor_post_locked: true, min_points_required: 0, is_quick_access: true,
    mobile_position: Number(position), bonus_points_per_post: 0,
  })) as CommunityTab[];
  return [...forumTabs, ...chatTabs, ...special];
}

/**
 * Fetches the access requirements (min points / min level) for a single tab.
 * Used by gated tabs (e.g. Desafios) to show lock state.
 */
export async function fetchTabAccessRequirements(
  tabId: string,
): Promise<{ min_points_required: number; min_level_id: string | null } | null> {
  const area = (await listAreas()).find(item => item.id === tabId);
  return area ? { min_points_required: 0, min_level_id: null } : null;
}

/**
 * Returns the reply-notification preference for a user. Defaults to enabled when missing.
 */
export async function fetchReplyNotifyPref(userId: string): Promise<boolean> {
  void userId;
  return true;
}

export async function fetchCommunityTags(): Promise<CommunityTag[]> {
  return [] as CommunityTag[];
}
